import { useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import axios from "axios";
import Navbar from "../components/Navbar";
import { CRICKET_API_URL } from "../config";
import BottomNavigation from "../components/BottomNavigation";
import { getDisplayMatchStatusMeta } from "../utils/matchStatusDisplay";

const MY_CONTESTS_CACHE_KEY = "myContestMatches_v2";
const EMPTY_MATCH_CATEGORIES = { upcoming: [], live: [], completed: [] };

const getMatchTimestamp = (match = {}) => {
    const rawTime = match.matchStartTime || match.startTime || match.event_date_start;

    if (!rawTime) return 0;
    if (typeof rawTime === "number") {
        return rawTime < 10000000000 ? rawTime * 1000 : rawTime;
    }

    try {
        let cleanDate = typeof rawTime === "string" ? rawTime.replace(" ", "T") : rawTime;
        if (typeof cleanDate === "string" && !cleanDate.endsWith("Z")) cleanDate += "Z";

        const timestamp = new Date(cleanDate).getTime();
        return Number.isNaN(timestamp) ? 0 : timestamp;
    } catch (error) {
        return 0;
    }
};

const getMyContestCategory = (match = {}) => {
    const displayStatusMeta = getDisplayMatchStatusMeta(match);
    const hasCreatedTeam = match.hasCreatedTeam === true || Number(match.teamCount || 0) > 0;
    const hasContestActivity = match.hasJoinedContest === true || Number(match.contestCount || 0) > 0;
    const hasUserActivity = hasCreatedTeam || hasContestActivity;

    if (!hasUserActivity) return null;
    if (["completed", "abandoned", "cancelled"].includes(displayStatusMeta.key)) return "completed";
    if (displayStatusMeta.key === "live") return hasCreatedTeam ? "live" : null;
    if (displayStatusMeta.key === "upcoming" || String(match.status || "").toLowerCase() === "lineup_out") return "upcoming";
    return null;
};

// --- ⏰ CLEAN TIMER WALA COMPONENT ---
const MatchStatus = ({ startTime, status, apiStatus, statusInfo }) => {
    const [label, setLabel] = useState("");

    useEffect(() => {
        const updateTimer = () => {
            const displayStatusMeta = getDisplayMatchStatusMeta({
                status,
                apiStatus,
                status_info: statusInfo,
            });

            if (displayStatusMeta.key === "completed") {
                setLabel("Completed");
                return;
            }
            if (displayStatusMeta.key === "abandoned") {
                setLabel("Abandoned");
                return;
            }
            if (displayStatusMeta.key === "cancelled") {
                setLabel("Cancelled");
                return;
            }
            if (displayStatusMeta.key === "live") {
                setLabel("LIVE");
                return;
            }

            try {
                let cleanDate = typeof startTime === 'string' ? startTime.replace(" ", "T") : startTime;
                if (typeof cleanDate === 'string' && !cleanDate.endsWith('Z')) cleanDate += 'Z';

                const start = new Date(cleanDate).getTime();
                const now = new Date().getTime();
                const diff = start - now;

                if (isNaN(start) || diff <= 0) {
                    setLabel("LIVE");
                } else {
                    const totalHours = Math.floor(diff / (1000 * 60 * 60));
                    const minutes = Math.floor((diff % (1000 * 60 * 60)) / (1000 * 60));
                    const seconds = Math.floor((diff % (1000 * 60)) / 1000);
                    setLabel(`${totalHours.toString().padStart(2, '0')}h ${minutes.toString().padStart(2, '0')}m ${seconds.toString().padStart(2, '0')}s`);
                }
            } catch (e) {
                setLabel("LIVE");
            }
        };

        updateTimer();
        const interval = setInterval(updateTimer, 1000);
        return () => clearInterval(interval);
    }, [apiStatus, startTime, status, statusInfo]);

    return <span>{label}</span>;
};

function MyContests() {
    const navigate = useNavigate();
    const [activeTab, setActiveTab] = useState("upcoming");

    let parsedData = EMPTY_MATCH_CATEGORIES;
    try {
        const savedData = localStorage.getItem(MY_CONTESTS_CACHE_KEY);
        parsedData = savedData ? JSON.parse(savedData) : EMPTY_MATCH_CATEGORIES;
    } catch (error) {
        parsedData = EMPTY_MATCH_CATEGORIES;
    }

    const [myMatches, setMyMatches] = useState(parsedData);
    const [loading, setLoading] = useState(parsedData === EMPTY_MATCH_CATEGORIES);

    useEffect(() => {
        const fetchUserMatches = async () => {
            const token = localStorage.getItem("token");
            if (!token) return navigate("/login");

            try {
                const res = await axios.get(`${CRICKET_API_URL}/api/cricket/my-contests/summary`, {
                    headers: { Authorization: `Bearer ${token}` }
                });

                if (res.data.success) {
                    const activityMatches = res.data.data || [];
                    const categorized = { upcoming: [], live: [], completed: [] };

                    activityMatches.forEach((match) => {
                        const category = getMyContestCategory(match);
                        if (category) {
                            categorized[category].push(match);
                        }
                    });

                    categorized.upcoming.sort((a, b) => getMatchTimestamp(a) - getMatchTimestamp(b));
                    categorized.live.sort((a, b) => getMatchTimestamp(b) - getMatchTimestamp(a));
                    categorized.completed.sort((a, b) => getMatchTimestamp(b) - getMatchTimestamp(a));

                    setMyMatches(categorized);
                    localStorage.setItem(MY_CONTESTS_CACHE_KEY, JSON.stringify(categorized));
                }
            } catch (error) {
                console.error("🚨 Error fetching matches:", error);
            } finally {
                setLoading(false);
            }
        };

        fetchUserMatches();
    }, [navigate]);

    const MatchCard = ({ match, type }) => {
        const t1 = match.team1 || match.homeTeam || {};
        const t2 = match.team2 || match.awayTeam || {};

        const team1 = {
            name: t1.name || match.teamA || match.name?.split(' vs ')[0] || "Team 1",
            code: t1.code || (t1.name?.slice(0, 3).toUpperCase()) || (match.name?.split(' vs ')[0]?.slice(0, 3).toUpperCase()) || "T1",
            image: t1.image || t1.logo || match.t1img || match.teamInfo?.[0]?.img || "https://h.cricapi.com/img/icon512.png"
        };

        const team2 = {
            name: t2.name || match.teamB || match.name?.split(' vs ')[1] || "Team 2",
            code: t2.code || (t2.name?.slice(0, 3).toUpperCase()) || (match.name?.split(' vs ')[1]?.slice(0, 3).toUpperCase()) || "T2",
            image: t2.image || t2.logo || match.t2img || match.teamInfo?.[1]?.img || "https://h.cricapi.com/img/icon512.png"
        };

        const seriesName = match.tournament || match.seriesName || "T20 Match";

        let timeVal = match.matchStartTime || match.startTime || match.event_date_start;
        if (typeof timeVal === 'number' && timeVal < 10000000000) timeVal = timeVal * 1000;

        const statusText = (match.status || "").toLowerCase();
        const isLineupsOut = match.lineupOut === true || match.lineupsOut === true || statusText === "lineup_out";
        const badgeMeta = getDisplayMatchStatusMeta(match);

        const team1Color = "#800000";
        const team2Color = "#800000";

        return (
            <div
                className="card border-0 shadow-sm mb-3 bg-white"
                style={{ borderRadius: "16px", cursor: "pointer", overflow: "hidden" }}
                onClick={() => {
                    // 🔥 MAIN NAVIGATION LOGIC 🔥
                    if (type === "upcoming") {
                        // Upcoming me contests list / team creation wale page par jayega
                        navigate(`/contests/${match.matchId || match.id || match._id}`, {
                            state: { matchData: match }
                        });
                    } else {
                        // Live aur Completed me match dashboard par jayega
                        navigate(`/match-dashboard/${match.matchId || match.id || match._id}`, {
                            state: { matchData: match }
                        });
                    }
                }}
            >
                {/* Header */}
                <div className="d-flex justify-content-between align-items-center px-3 py-2 border-bottom" style={{ borderColor: "#f0f0f0" }}>
                    <small className="text-secondary fw-bold text-uppercase text-truncate" style={{ fontSize: "11px", letterSpacing: "0.5px", maxWidth: "70%" }}>
                        {seriesName}
                    </small>
                    <span className={`badge ${badgeMeta.className || ""}`} style={{ fontSize: "10px", padding: "4px 8px", ...(badgeMeta.style || {}) }}>
                        {badgeMeta.label}
                    </span>
                </div>

                {/* Body UI */}
                <div className="card-body py-3 px-2">
                    <div className="d-flex justify-content-between align-items-center px-2">

                        {/* Team 1 */}
                        <div className="d-flex flex-column align-items-center" style={{ width: '35%' }}>
                            <div className="position-relative">
                                <div className="rounded-circle d-flex align-items-center justify-content-center shadow-sm"
                                    style={{
                                        width: "56px", height: "56px",
                                        background: `linear-gradient(135deg, ${team1Color} 0%, #000 100%)`,
                                        border: "3px solid #fff", color: "#fff", fontWeight: "800", fontSize: "14px", overflow: "hidden"
                                    }}
                                >
                                    <img src={team1.image} alt={team1.code} className="w-100 h-100 rounded-circle object-fit-cover bg-white" />
                                </div>
                                <div className="position-absolute bottom-0 start-50 translate-middle-x bg-white px-2 rounded-pill border shadow-sm text-dark fw-bold" style={{ fontSize: '9px', whiteSpace: 'nowrap', marginTop: '-10px' }}>
                                    {team1.code}
                                </div>
                            </div>
                            <div className="fw-bold text-dark text-truncate w-100 text-center mt-2" style={{ fontSize: "13px" }}>
                                {team1.name}
                            </div>
                        </div>

                        {/* VS / Timer Center */}
                        <div className="text-center d-flex flex-column align-items-center justify-content-center" style={{ width: '30%' }}>
                            <span className="text-secondary opacity-25 fw-black" style={{ fontSize: "28px", fontFamily: 'sans-serif', fontWeight: "900", lineHeight: "1" }}>VS</span>

                            {type === "upcoming" ? (
                                <div className="mt-1 d-flex flex-column align-items-center">
                                    <div className="d-flex align-items-center gap-1 text-danger bg-danger-subtle px-2 py-1 rounded-pill" style={{ fontSize: '10px', fontWeight: '600' }}>
                                        <i className="bi bi-stopwatch"></i>
                                        <MatchStatus startTime={timeVal} status={match.status} apiStatus={match.apiStatus} statusInfo={match.status_info} />
                                    </div>

                                    {/* 🔥 LINEUPS OUT BADGE 🔥 */}
                                    {isLineupsOut && (
                                        <div className="text-success mt-1" style={{ fontSize: "10px", fontWeight: "800", letterSpacing: "0.5px" }}>
                                            ● LINEUPS OUT
                                        </div>
                                    )}
                                </div>
                            ) : (
                                <div className="mt-1 badge bg-light text-dark border px-3 py-1 rounded-pill" style={{ fontSize: '10px' }}>
                                    <MatchStatus startTime={timeVal} status={match.status} apiStatus={match.apiStatus} statusInfo={match.status_info} />
                                </div>
                            )}
                        </div>

                        {/* Team 2 */}
                        <div className="d-flex flex-column align-items-center" style={{ width: '35%' }}>
                            <div className="position-relative">
                                <div className="rounded-circle d-flex align-items-center justify-content-center shadow-sm"
                                    style={{
                                        width: "56px", height: "56px",
                                        background: `linear-gradient(135deg, ${team2Color} 0%, #000 100%)`,
                                        border: "3px solid #fff", color: "#fff", fontWeight: "800", fontSize: "14px", overflow: "hidden"
                                    }}
                                >
                                    <img src={team2.image} alt={team2.code} className="w-100 h-100 rounded-circle object-fit-cover bg-white" />
                                </div>
                                <div className="position-absolute bottom-0 start-50 translate-middle-x bg-white px-2 rounded-pill border shadow-sm text-dark fw-bold" style={{ fontSize: '9px', whiteSpace: 'nowrap', marginTop: '-10px' }}>
                                    {team2.code}
                                </div>
                            </div>
                            <div className="fw-bold text-dark text-truncate w-100 text-center mt-2" style={{ fontSize: "13px" }}>
                                {team2.name}
                            </div>
                        </div>

                    </div>
                </div>

                {/* Footer */}
                <div className="px-3 py-2 border-top d-flex justify-content-between align-items-center" style={{ backgroundColor: "#fafafa", borderColor: "#f0f0f0" }}>
                    <small className="text-muted fw-medium" style={{ fontSize: "11px" }}>
                        <i className="bi bi-people-fill me-1"></i> {match.teamCount || 0} Team{(match.teamCount || 0) > 1 ? 's' : ''} Created
                    </small>
                    <small className="text-muted fw-medium" style={{ fontSize: "11px" }}>
                        <i className="bi bi-trophy-fill text-warning me-1"></i> {match.contestCount || 0} Contest{(match.contestCount || 0) !== 1 ? 's' : ''} Joined
                    </small>
                </div>
            </div>
        );
    };

    const activeList = myMatches[activeTab] || [];

    if (loading) return (<></>);

    return (
        <>
            <Navbar />
            <div style={{ minHeight: "100vh", background: "#f0f2f5", paddingBottom: "90px" }} className="mt-5 pt-3">
                <div className="bg-white sticky-top shadow-sm">
                    <div className="d-flex justify-content-between text-center">
                        {["upcoming", "live", "completed"].map((tab) => (
                            <button
                                key={tab}
                                className={`flex-grow-1 border-0 py-3 fw-bold text-uppercase bg-white ${activeTab === tab ? "text-dark" : "text-secondary"}`}
                                onClick={() => setActiveTab(tab)}
                                style={{ fontSize: "13px", letterSpacing: "0.5px", position: "relative" }}
                            >
                                {tab}
                                {activeTab === tab && <div className="position-absolute bottom-0 start-0 w-100" style={{ height: "3px", borderRadius: "10px 10px 0 0", backgroundColor: "#800000" }}></div>}
                            </button>
                        ))}
                    </div>
                </div>

                <div className="py-3 container-fluid px-3">
                    {activeList.length > 0 ? (
                        activeList.map((match, idx) => <MatchCard key={match.matchId || match.id || match._id || idx} match={match} type={activeTab} />)
                    ) : (
                        <div className="text-center py-5 mt-5">
                            <div className="mb-3 opacity-25"><i className="bi bi-trophy" style={{ fontSize: "3rem" }}></i></div>
                            <h6 className="fw-bold text-dark">No {activeTab} Matches</h6>
                            <p className="text-muted small px-4">You haven't created a team or joined a contest in this category yet.</p>
                            {activeTab === 'upcoming' && (
                                <button className="btn rounded-pill px-4 mt-3 shadow-sm fw-medium text-white" style={{ backgroundColor: "#800000" }} onClick={() => navigate("/home")}>
                                    View Upcoming Matches
                                </button>
                            )}
                        </div>
                    )}
                </div>
            </div>
            <BottomNavigation />
        </>
    );
}

export default MyContests;
